// The READ overlay and the WRITE fold over `runners.capabilities`:
// `effectiveProvisions` re-decides the platform-reserved tokens every pass, and
// `mergeProvisions` folds a fresh advert into what the column already holds.
// Split out of `../runner.ts`, which is now the barrel over this directory.

import {
  boxKekCapable,
  boxModelCallCapable,
  boxModeNarrowingCapable,
  boxPlanAdmissionCapable,
  boxPlannerCapable,
  boxSetupRecipesCapable,
} from './box-kek';
import type { RunnerCapabilities } from './capabilities';
import { isPrivilegedProvision, RunnerCapability, SERVICE_CAPABILITY_PREFIX } from './capabilities';

/**
 * A box's capability tokens AS THE ASSIGNER SEES THEM: what setup mode advertised
 * (`runners.capabilities.provisions`) plus what the platform derives from the
 * build it reports. The single seam the three DISPATCH gates read — the claim
 * path, the lane arbiter and the capability-escalation sweep — so those three can
 * never disagree about what a box can do. NOT "every capability gate": the
 * isolation and CI gates read the raw column by design, which the paragraph below
 * spells out. That universal stood in this very docstring's opening line while its
 * body refuted it, which is how a reader ends up deleting the read-filter. Pure; allocates a fresh array, never mutates the
 * stored set, and re-derives on every call — so a box that reports a LOWER version
 * loses the token on the next pass. (A box that stops reporting a version at all
 * keeps it: see the freshness caveat in the header. "Withdraws on downgrade" is a
 * property of re-derivation PLUS a fresh column, and only the first half is here.)
 *
 * **Every {@link PRIVILEGED_PROVISIONS} token is PLATFORM-RESERVED: a stored copy
 * is dropped, never honoured.** `capabilities.provisions` is an untrusted box's one
 * reach into a dispatch-gate column — `CapabilityTokenSchema` gates the token's
 * SHAPE over a deliberately OPEN vocabulary (and with the `/i` flag, hence the
 * case-fold in {@link isPrivilegedProvision}) — and `mergeProvisions` is additive
 * with no removal path. So a box that advertised one would hold it permanently, and
 * for `secrets:sealed` would defeat the withdraw-on-downgrade property that
 * justifies deriving at read at all. Filtering them out of `stored` is what makes
 * "the platform decides these" true.
 *
 * **This filter is now the SECOND of two gates, not the whole reservation.** The
 * matching refusal at the WRITE seam ({@link mergeProvisions}) was built, cut, and
 * has been rebuilt: cutting it was survivable while `secrets:sealed` was the only
 * reserved token and the read filter covered it, but `isolation:container` was
 * reserved by NOTHING and its raw-column reader believed the box. This filter
 * therefore keeps a job the write seam cannot do — a row written BEFORE the
 * refusal existed still holds its stale token, and only the read makes it inert.
 *
 * Not every capability gate reads through here, and the reason that is safe is a
 * PROPERTY, not a count (two earlier drafts of this paragraph said "two", which was
 * wrong in the already-safe direction and needed re-counting every time a CI reader
 * was added): **every remaining raw-column reader matches a CLOSED token set that
 * cannot contain a reserved token.** The set is the CI backfill path
 * (`filterByCapability` / `admitCiBackfill`), and `ciRequiredCapabilities` only ever
 * emits `developerz-`-prefixed labels — an unnamespaced vocabulary that cannot spell
 * `isolation:` or `secrets:`. `isBoxIsolated` used to be the counterexample that
 * broke this property: it read the raw column for a token a box adverts about
 * itself. It no longer reads `capabilities` at all — isolation is an operator
 * attestation (`runners.isolation_attested_at`), which is what makes the sentence
 * above true rather than merely intended.
 */
export function effectiveProvisions(
  capabilities: RunnerCapabilities | null | undefined,
  runnerVersion: string | null | undefined,
): string[] {
  // Drop every self-advertised RESERVED token before deciding — see the note above.
  // Case-insensitive inside `isPrivilegedProvision`, for the same reason the write
  // guard is: the wire token regex is `/i`, so a legacy row may hold `SECRETS:SEALED`.
  const stored = (capabilities?.provisions ?? []).filter((token) => !isPrivilegedProvision(token));
  const derived: string[] = [];
  if (boxKekCapable(runnerVersion)) derived.push(RunnerCapability.SealedSecrets);
  if (boxModeNarrowingCapable(runnerVersion)) derived.push(RunnerCapability.ModeNarrowing);
  if (boxPlanAdmissionCapable(runnerVersion)) derived.push(RunnerCapability.PlanAdmission);
  if (boxModelCallCapable(runnerVersion)) derived.push(RunnerCapability.ModelCall);
  if (boxPlannerCapable(runnerVersion)) derived.push(RunnerCapability.Planner);
  if (boxSetupRecipesCapable(runnerVersion)) derived.push(RunnerCapability.SetupRecipes);
  return [...stored, ...derived];
}

/** {@link effectiveProvisions} in `RunnerCapabilities` shape — for the gates that match a whole box. */
export function effectiveCapabilities(
  capabilities: RunnerCapabilities | null | undefined,
  runnerVersion: string | null | undefined,
): RunnerCapabilities {
  return {
    repos: capabilities?.repos ?? [],
    provisions: effectiveProvisions(capabilities, runnerVersion),
  };
}

/**
 * Ceiling on a box's `provisions` set (arc 101 doc 03). A box accumulates tokens
 * across every repo setup mode runs on it, and the tokens arrive from the BOX —
 * untrusted input written into a jsonb column nothing else bounds. Generous
 * enough that a real box (a handful of stacks × ~4 tokens each) never hits it.
 */
export const RUNNER_MAX_PROVISIONS = 64;

/** Outcome of folding freshly-detected tokens into a box's existing `provisions`. */
export type ProvisionMerge = {
  /** The merged set to persist — existing tokens first, in their original order. */
  provisions: string[];
  /** Tokens this merge ADDED (empty ⇒ nothing changed; the caller can skip the write). */
  added: string[];
  /** Detected tokens refused because the merge would exceed {@link RUNNER_MAX_PROVISIONS}. */
  dropped: string[];
  /**
   * Detected tokens refused as PRIVILEGED ({@link isPrivilegedProvision}) — a box
   * asserting a capability only the platform may grant. Separate from `dropped`
   * because the two mean opposite things to whoever reads the alert: `dropped` is
   * a box that outgrew its ceiling, `refused` is a box that reached for tenancy.
   */
  refused: string[];
};

/**
 * Fold the capability tokens a box just DETECTED into what it already advertises
 * (arc 101 doc 03 — the write half of setup mode's `capabilities_detected`).
 *
 * **Union, never replace.** A box is set up for many repos over its life; the
 * browsers it installed for repo A are still installed when it later provisions
 * repo B's python toolchain. Replacing would silently un-advertise a real
 * capability and strand every task that requires it.
 *
 * **Additive only.** There is no removal path here on purpose: the event says
 * "these were provisioned", never "these are gone", so absence is not evidence.
 * A capability that genuinely disappears is an operator/re-enroll concern.
 *
 * **PRIVILEGED tokens are refused, and this is the seam that refuses them.** The
 * detected set arrives from an UNTRUSTED box, and `runners.capabilities` is its
 * only reach into a dispatch column — so a token that widens tenancy or trust
 * ({@link isPrivilegedProvision}) is dropped into `refused` and never persisted.
 * The guard lives in the PRIMITIVE rather than in `mergeRunnerProvisions`, its one
 * caller today, because the primitive is what a second capability writer would
 * reuse: putting it here means the next writer inherits the fence instead of
 * having to remember it. `existing` is deliberately NOT filtered — additive-only
 * is the contract, and a legacy row's stale privileged token is made inert at the
 * READ seam ({@link effectiveProvisions}) instead of by a write this event never
 * asked for.
 *
 * Existing tokens keep their position (a stable jsonb value ⇒ a no-op merge is
 * byte-identical), new ones append in first-seen order, and the overflow past
 * {@link RUNNER_MAX_PROVISIONS} is REPORTED in `dropped` rather than silently
 * truncated. Pure — the merge primitive the fleet consumer and any future
 * capability writer share.
 */
export function mergeProvisions(
  existing: readonly string[],
  detected: readonly string[],
  max: number = RUNNER_MAX_PROVISIONS,
): ProvisionMerge {
  const provisions: string[] = [];
  const seen = new Set<string>();
  for (const token of existing) {
    if (seen.has(token)) continue;
    seen.add(token);
    provisions.push(token);
  }
  const added: string[] = [];
  const dropped: string[] = [];
  const refused: string[] = [];
  for (const token of detected) {
    // BEFORE the dedup and the cap: a privileged token must be refused even when
    // the box already carries it (a legacy row) or the set is full, so the alert
    // fires on the ATTEMPT rather than on whether the row happened to have room.
    if (isPrivilegedProvision(token)) {
      if (!refused.includes(token)) refused.push(token);
      continue;
    }
    if (seen.has(token)) continue;
    if (provisions.length >= max) {
      if (!dropped.includes(token)) dropped.push(token);
      continue;
    }
    seen.add(token);
    provisions.push(token);
    added.push(token);
  }
  return { provisions, added, dropped, refused };
}

/**
 * The ONE retraction a presence beat may make (#4333, #6474): drop each stored
 * `service:*` token the box's CURRENT advert no longer carries.
 *
 * **Only the `service:` family, because only it is a live probe the beat carries
 * whole.** The box re-runs `reachableServices` over every `SERVICE_PORTS` entry on
 * `CAPABILITY_REFRESH_MS` and adverts the set it reached, so a stored service token
 * absent from that advert is a TCP connect that stopped answering — the measured
 * case: boxes holding `service:postgres` while 127.0.0.1:5432 refused, claiming
 * service-gated work their run-start probe then discarded. Every other token may be
 * a fact a SETUP run wrote that the beat never carries (the {@link mergeProvisions}
 * "absence is not evidence" rule), so it is kept whatever the advert says.
 *
 * Pure; order-preserving. The caller folds `detected` into `kept` with
 * {@link mergeProvisions}, so a still-reachable service is never re-added twice.
 */
export function retractUnadvertisedServices(
  existing: readonly string[],
  detected: readonly string[],
): { kept: string[]; retracted: string[] } {
  const advertised = new Set(detected);
  const kept: string[] = [];
  const retracted: string[] = [];
  for (const token of existing) {
    if (token.startsWith(SERVICE_CAPABILITY_PREFIX) && !advertised.has(token)) {
      retracted.push(token);
    } else {
      kept.push(token);
    }
  }
  return { kept, retracted };
}
